import { FC } from 'react'
import { Route, Routes, Navigate, useLocation } from 'react-router-dom'
import { MasterLayout } from '../../_payairo/layout/MasterLayout'
import { DashboardWrapper } from '../pages/dashboard/DashboardWrapper'
import { lazyRoute } from './lazyRoute'
import { UserManagementWrapper } from '../pages/userManagement/UserManagement'

import { RolesManagementWrapper } from '../pages/rolesManagement/RolesManagement'
import { useAuth } from '../modules/auth'
import { getDashboardPath } from '../utils/helper'
import { ProtectedRoute } from '../App'
import ManagedModuleAssets from '../pages/managedModuleAssets/ManagedModuleAssets'
import { ENABLE_NOTIFICATIONS } from '../hooks/useNotifications'

// Route guard component to redirect /dashboard to the actual super-admin dashboard
const DashboardRouteGuard: FC<{ children: React.ReactElement }> = ({ children }) => {
  const location = useLocation()
  const path = location.pathname

  if (
    path === '/dashboard' ||
    path === '/customer-support/dashboard' ||
    path === '/spectator/dashboard' ||
    path === '/manager/dashboard'
  ) {
    return <Navigate to='/super-admin/dashboard' replace />
  }

  return children
}

const RequirePermission: FC<{ permission: string | string[], requireAll?: boolean, children: React.ReactElement }> = ({
  permission,
  requireAll = false,
  children
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = useAuth()
  let isAllowed = true

  if (typeof permission === 'string') {
    isAllowed = hasPermission(permission)
  } else if (Array.isArray(permission) && permission.length > 0) {
    isAllowed = requireAll ? hasAllPermissions(permission) : hasAnyPermission(permission)
  }

  if (!isAllowed) {
    return <Navigate to='/error/403' replace />
  }

  return children
}

/** Module-level lazy routes: stable component identity + chunk retry (see `lazyRoute.ts`). */
const AnnouncementManagement = lazyRoute(() => import('../pages/announcement/AnnouncementManagement'))
const PayAiroUserManagement = lazyRoute(() => import('../pages/payairouser/PayAiroUserManagement'))
const SetLimitsWrapper = lazyRoute(() => import('../pages/setLimits/SetLimits'))
const FeeSettingsWrapper = lazyRoute(() => import('../pages/feeSettings/FeeSettings'))
const SupportCenterWrapper = lazyRoute(() => import('../pages/supportCenter/Support'))
const TransactionHistoryWrapper = lazyRoute(() => import('../pages/transactions/AllTransactions'))
const UserDetailsPage = lazyRoute(() => import('../pages/userDetails/UserDetailsPage'))
const UserTransactionsPage = lazyRoute(() => import('../pages/payairouser/UserTransactionsPage'))
const MyProfileWrapper = lazyRoute(() => import('../pages/myProfile/MyProfile'))
const CryptoTransactionHistoryWrapper = lazyRoute(() => import('../pages/transactions/CryptoTransactions'))
const NotificationsPage = lazyRoute(() => import('../pages/notifications/NotificationsPage'))
const SupportPerformanceWrapper = lazyRoute(() => import('../pages/supportPerformance/SupportPerformance'))
const SmsProviders = lazyRoute(() => import('../pages/Settings/SmsProviders'))
const SDUILayoutEditor = lazyRoute(() => import('../pages/sduiLayoutEditor/SDUILayoutEditor'))
const SduiAssetManager = lazyRoute(() => import('../pages/sduiAssetManager/SduiAssetManager'))
const BlogManagementWrapper = lazyRoute(() => import('../pages/blogManagement/BlogManagementWrapper'))
const BlogList = lazyRoute(() => import('../pages/blogManagement/BlogList'))
const BlogEdit = lazyRoute(() => import('../pages/blogManagement/BlogEdit'))
const RewardsConfigurationPage = lazyRoute(() => import('../modules/rewards/pages/RewardsConfigurationPage'))
const PartnersManagementPage = lazyRoute(() => import('../modules/rewards/pages/PartnersManagementPage'))
const UserCryptoBalancesPage = lazyRoute(() => import('../pages/cryptoBalances/UserCryptoBalancesPage'))


const PrivateRoutes = () => {
  const { currentUser, auth } = useAuth()
  const role = currentUser?.role || auth?.role || currentUser?.user_type
  const dashboardPath = getDashboardPath(role, currentUser?.user_type)

  return (
    <>
      <Routes>
        <Route element={<MasterLayout />}>
          {/* Redirect to Dashboard after success login/registartion */}
          <Route path='auth/*' element={<Navigate to={dashboardPath} replace />} />
          {/* Pages */}
          {/* Super Admin Dashboard */}
          <Route
            path='dashboard'
            element={
              <DashboardRouteGuard>
                <DashboardWrapper />
              </DashboardRouteGuard>
            }
          />
          <Route
            path='super-admin/dashboard'
            element={
              <DashboardRouteGuard>
                <DashboardWrapper />
              </DashboardRouteGuard>
            }
          />
          <Route
            path='all-users'
            element={
              <RequirePermission permission='admin.staff.list'>
                <UserManagementWrapper />
              </RequirePermission>
            }
          />
          <Route
            path='access-management'
            element={
              <RequirePermission permission='admin.staff.list'>
                <UserManagementWrapper />
              </RequirePermission>
            }
          />

          <Route
            path='role-management'
            element={
              <RequirePermission permission='admin.roles.read'>
                <RolesManagementWrapper />
              </RequirePermission>
            }
          />

          <Route
            path='transactions'
            element={<Navigate to='/transactions/fiat' replace />}
          />
          <Route
            path='transactions/fiat'
            element={
              <RequirePermission permission='admin.transactions.list'>
                <TransactionHistoryWrapper />
              </RequirePermission>
            }
          />
          <Route
            path='transactions/crypto'
            element={
              <RequirePermission permission='admin.transactions.list'>
                <CryptoTransactionHistoryWrapper />
              </RequirePermission>
            }
          />
          <Route
            path='crypto-balances'
            element={
              <RequirePermission permission='admin.dashboard.stats'>
                <UserCryptoBalancesPage />
              </RequirePermission>
            }
          />
          <Route
            path='settings'
            element={
              <RequirePermission permission='admin.profile.view'>
                <MyProfileWrapper />
              </RequirePermission>
            }
          />
          <Route
            path='sms-providers'
            element={
              <RequirePermission permission='admin.sms_provider_settings'>
                <SmsProviders />
              </RequirePermission>
            }
          />
          <Route
            path='managed-module-assets'
            element={
              <RequirePermission permission='admin.sdui.assets'>
                <ManagedModuleAssets />
              </RequirePermission>
            }
          />
          <Route
            path='sdui-layout-editor'
            element={
              <RequirePermission permission='admin.sdui.layouts'>
                <SDUILayoutEditor />
              </RequirePermission>
            }
          />
          <Route
            path='sdui-asset-manager'
            element={
              <RequirePermission permission='admin.sdui.assets'>
                <SduiAssetManager />
              </RequirePermission>
            }
          />
          <Route
            path='rewards/configuration'
            element={
              <RequirePermission permission='admin.rewards.config'>
                <RewardsConfigurationPage />
              </RequirePermission>
            }
          />
          <Route
            path='rewards/partners'
            element={
              <RequirePermission permission='admin.rewards.partners'>
                <PartnersManagementPage />
              </RequirePermission>
            }
          />
          <Route
            path='blog-access'
            element={
              <RequirePermission permission='admin.blog.list'>
                <BlogManagementWrapper />
              </RequirePermission>
            }
          >
            <Route index element={<BlogList />} />
            <Route path='new' element={<BlogEdit />} />
            <Route path='edit/:id' element={<BlogEdit />} />
          </Route>
          <Route
            path='admin/support-performance'
            element={
              <RequirePermission permission='admin.support.performance'>
                <SupportPerformanceWrapper />
              </RequirePermission>
            }
          />
          <Route
            path='manager/support-performance'
            element={
              <RequirePermission permission='admin.support.performance'>
                <DashboardRouteGuard>
                  <SupportPerformanceWrapper />
                </DashboardRouteGuard>
              </RequirePermission>
            }
          />
          <Route
            path='announcements'
            element={
              <RequirePermission permission='admin.announcements.list'>
                <AnnouncementManagement />
              </RequirePermission>
            }
          />
          <Route
            path='payairo-users'
            element={
              <RequirePermission permission='admin.users.list'>
                <PayAiroUserManagement />
              </RequirePermission>
            }
          />
          <Route
            path='set-limits'
            element={
              <RequirePermission permission='admin.limits.manage'>
                <SetLimitsWrapper />
              </RequirePermission>
            }
          />
          <Route
            path='fee-settings'
            element={
              <RequirePermission permission='admin.fees.manage'>
                <FeeSettingsWrapper />
              </RequirePermission>
            }
          />
          <Route
            path='support'
            element={
              <RequirePermission permission='admin.guest_queries.list'>
                <SupportCenterWrapper />
              </RequirePermission>
            }
          />
          {ENABLE_NOTIFICATIONS && (
            <Route
              path='notifications'
              element={
                <RequirePermission permission='admin.notifications.list'>
                  <NotificationsPage />
                </RequirePermission>
              }
            />
          )}
          <Route
            path='user/:id'
            element={
              <RequirePermission permission='admin.users.list'>
                <UserDetailsPage />
              </RequirePermission>
            }
          />
          <Route
            path='user/:id/transactions'
            element={
              <RequirePermission permission='admin.transactions.list'>
                <UserTransactionsPage />
              </RequirePermission>
            }
          />
          <Route
            path='user-transactions/:id'
            element={
              <RequirePermission permission='admin.transactions.list'>
                <UserTransactionsPage />
              </RequirePermission>
            }
          />

          {/* <Route path='urlregistration' element={<UrlRegistration />} />
        <Route path='urlmapping' element={<UrlMapping />} />
        <Route path='myip' element={<MyIP />} />
        <Route path='vendorlist' element={<VendorListWrapper />} />
        <Route path='manageroutes' element={<ManageRoutes />} />
        <Route path='manageroutetree' element={<ManageRoutesTree />} /> */}
          {/* <Route path='ucccomplaints' element={<UccComplaintsWrapper />} /> */}
          {/* <Route path='smspack' element={<SMSPackWrapper />} /> */}
          {/* <Route path='orderslist' element={<OrdersListWrapper />} />
        <Route path='invoice' element={<InvoiceWrapper />} />
        <Route path='bulk-report' element={<BulkReport />} /> */}

          {/* Lazy Modules */}
          {/* <Route
          path='crafted/pages/profile/*'
          element={
            <SuspensedView>
              <ProfilePage />
            </SuspensedView>
          }
        />
        <Route
          path='crafted/pages/wizards/*'
          element={
            <SuspensedView>
              <WizardsPage />
            </SuspensedView>
          }
        />
        <Route
          path='crafted/widgets/*'
          element={
            <SuspensedView>
              <WidgetsPage />
            </SuspensedView>
          }
        />
        <Route
          path='crafted/account/*'
          element={
            <SuspensedView>
              <AccountPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/chat/*'
          element={
            <SuspensedView>
              <ChatPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/user-management/*'
          element={
            <SuspensedView>
              <UsersPage />
            </SuspensedView>
          }
        /> */}
          {/* Page Not Found */}
          <Route path='*' element={<Navigate to='/error/404' />} />
        </Route>

        {/* <Route element={<PaymentLayout />}>
        <Route path='payment' element={<Payment />} />
      </Route> */}
      </Routes>
    </>
  )
}

export { PrivateRoutes }
